import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import SiteHeader from "@/components/feature/SiteHeader";
import Footer from "@/pages/home/components/Footer";

const SKILLS = [
  { label: "Physical Labor", level: 95, color: "bg-emerald-500" },
  { label: "Landscaping",    level: 80, color: "bg-emerald-400" },
  { label: "Cleaning",       level: 88, color: "bg-sky-400"     },
  { label: "Driving (CDL)",  level: 70, color: "bg-orange-400"  },
  { label: "Event Setup",    level: 65, color: "bg-amber-400"   },
  { label: "Moving / Lift",  level: 92, color: "bg-green-500"   },
];

const PAST_JOBS = [
  { id: 1, title: "Warehouse Loader",    company: "FastFreight Co.",  date: "Mar 18, 2026", hours: 8,  earned: "$224", rating: 5, status: "Completed" },
  { id: 2, title: "Event Setup Crew",    company: "EventPro LLC",     date: "Mar 14, 2026", hours: 6,  earned: "$150", rating: 5, status: "Completed" },
  { id: 3, title: "Office Deep Clean",   company: "CleanSpace Inc.",  date: "Mar 10, 2026", hours: 5,  earned: "$110", rating: 4, status: "Completed" },
  { id: 4, title: "Landscaping Crew",    company: "GreenCut Lawns",   date: "Mar 5, 2026",  hours: 7,  earned: "$196", rating: 5, status: "Completed" },
  { id: 5, title: "Furniture Assembly",  company: "HomeStart Moving", date: "Feb 28, 2026", hours: 4,  earned: "$108", rating: 5, status: "Completed" },
];

const ACHIEVEMENTS = [
  { icon: "ri-star-fill",      label: "5-Star Worker",      color: "text-amber-500"  },
  { icon: "ri-shield-check-fill", label: "Background Checked", color: "text-emerald-600" },
  { icon: "ri-flashlight-fill", label: "Same-Day Ready",    color: "text-sky-500"    },
  { icon: "ri-trophy-fill",    label: "Top 10% Earner",     color: "text-orange-500" },
];

export default function WorkerProfilePage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [availableNow, setAvailableNow] = useState(true);
  const [activeTab, setActiveTab] = useState<"about" | "jobs" | "skills">("about");

  if (!user || user.type !== "worker") {
    return (
      <div className="min-h-screen bg-[#fafafa] flex flex-col">
        <SiteHeader />
        <div className="flex-1 flex items-center justify-center pt-20">
          <div className="text-center">
            <i className="ri-lock-line text-4xl text-gray-400 mb-3" />
            <h2 className="text-xl font-bold text-gray-900 mb-2">Worker account required</h2>
            <p className="text-sm text-gray-500 mb-5">Please sign in as a Worker to view this page.</p>
            <Link to="/login" className="px-5 py-2.5 bg-emerald-500 text-white text-sm font-bold rounded-lg hover:bg-emerald-600 transition-colors whitespace-nowrap">
              Sign In
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const handleLogout = () => { logout(); navigate("/"); };

  return (
    <div className="min-h-screen bg-[#fafafa]">
      <SiteHeader />

      {/* Profile Hero */}
      <section className="pt-20">
        <div className="h-44 relative overflow-hidden">
          <img
            src="https://public.readdy.ai/ai/img_res/c49b2b2c1a4541460118467cc1999d9a.jpg"
            alt=""
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-emerald-900/50 to-transparent" />
        </div>
        <div className="max-w-7xl mx-auto px-4 md:px-8 -mt-14 relative">
          <div className="flex flex-col md:flex-row md:items-end gap-5 pb-6 border-b border-gray-100">
            <div className="relative">
              <img
                src={user.avatar}
                alt={user.name}
                className="w-24 h-24 rounded-2xl object-cover border-4 border-white"
              />
              <span className={`absolute -bottom-1 -right-1 w-4 h-4 rounded-full border-2 border-white ${availableNow ? "bg-emerald-500" : "bg-gray-400"}`} />
            </div>
            <div className="flex-1">
              <div className="flex flex-wrap items-center gap-2 mb-1">
                <h1 className="text-2xl font-extrabold text-gray-900">{user.name}</h1>
                <span className="text-xs font-bold bg-emerald-50 text-emerald-700 px-2.5 py-1 rounded-full">
                  Worker
                </span>
                {availableNow && (
                  <span className="text-xs font-bold bg-green-100 text-green-700 px-2.5 py-1 rounded-full flex items-center gap-1">
                    <span className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse" />
                    Available Now
                  </span>
                )}
              </div>
              <p className="text-sm text-gray-500">General Labor · Landscaping · Event Setup · Cleaning</p>
              <div className="flex flex-wrap gap-4 mt-2 text-xs text-gray-500">
                <span className="flex items-center gap-1"><i className="ri-map-pin-line" />Brooklyn, NY</span>
                <span className="flex items-center gap-1"><i className="ri-calendar-line" />Joined Jan 2026</span>
                <span className="flex items-center gap-1"><i className="ri-star-fill text-amber-400" />4.9 rating (23 reviews)</span>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <button
                onClick={() => setAvailableNow(!availableNow)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-bold border transition-colors cursor-pointer whitespace-nowrap ${
                  availableNow
                    ? "border-emerald-300 bg-emerald-50 text-emerald-700 hover:bg-emerald-100"
                    : "border-gray-200 bg-white text-gray-600 hover:bg-gray-50"
                }`}
              >
                <span className={`w-2 h-2 rounded-full ${availableNow ? "bg-emerald-500 animate-pulse" : "bg-gray-400"}`} />
                {availableNow ? "Available" : "Unavailable"}
              </button>
              <Link
                to="/dashboard"
                className="px-4 py-2 rounded-lg text-xs font-bold border border-gray-200 bg-white text-gray-700 hover:bg-gray-50 transition-colors whitespace-nowrap"
              >
                <i className="ri-dashboard-line mr-1" />Dashboard
              </Link>
              <button
                onClick={handleLogout}
                className="px-4 py-2 rounded-lg text-xs font-bold border border-red-100 bg-red-50 text-red-600 hover:bg-red-100 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-logout-box-line mr-1" />Sign Out
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-8">
        <div className="max-w-7xl mx-auto px-4 md:px-8 grid grid-cols-1 lg:grid-cols-3 gap-7">

          {/* Left: Stats + Achievements */}
          <div className="flex flex-col gap-5">
            {/* Stats */}
            <div className="bg-white rounded-2xl border border-gray-100 p-5">
              <h3 className="font-bold text-gray-900 text-sm mb-4">Performance</h3>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { label: "Total Earned",  value: "$4,240", icon: "ri-money-dollar-circle-line", color: "text-green-600", bg: "bg-green-50"   },
                  { label: "Jobs Done",     value: "38",     icon: "ri-check-double-line",         color: "text-sky-600",   bg: "bg-sky-50"    },
                  { label: "Avg Rating",    value: "4.9 ★",  icon: "ri-star-line",                 color: "text-amber-600", bg: "bg-amber-50"  },
                  { label: "On-Time Rate",  value: "98%",    icon: "ri-time-line",                 color: "text-emerald-600", bg: "bg-emerald-50" },
                ].map(s => (
                  <div key={s.label} className="bg-gray-50 rounded-xl p-3">
                    <div className={`w-7 h-7 flex items-center justify-center rounded-lg ${s.bg} mb-1.5`}>
                      <i className={`${s.icon} ${s.color} text-xs`} />
                    </div>
                    <div className="text-base font-extrabold text-gray-900">{s.value}</div>
                    <div className="text-xs text-gray-500">{s.label}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Achievements */}
            <div className="bg-white rounded-2xl border border-gray-100 p-5">
              <h3 className="font-bold text-gray-900 text-sm mb-4">Achievements</h3>
              <div className="flex flex-col gap-2.5">
                {ACHIEVEMENTS.map(a => (
                  <div key={a.label} className="flex items-center gap-3">
                    <div className="w-8 h-8 flex items-center justify-center rounded-xl bg-gray-50">
                      <i className={`${a.icon} ${a.color} text-sm`} />
                    </div>
                    <span className="text-xs font-semibold text-gray-700">{a.label}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Availability */}
            <div className="bg-gradient-to-br from-emerald-50 to-green-50 rounded-2xl border border-emerald-100 p-5">
              <h3 className="font-bold text-gray-900 text-sm mb-3">Schedule</h3>
              <div className="flex flex-col gap-2 text-xs">
                {[
                  { day: "Mon–Fri", time: "6am – 8pm", active: true },
                  { day: "Saturday", time: "7am – 4pm", active: true },
                  { day: "Sunday",  time: "Unavailable", active: false },
                ].map(s => (
                  <div key={s.day} className="flex items-center justify-between">
                    <span className="font-semibold text-gray-700">{s.day}</span>
                    <span className={s.active ? "text-emerald-700 font-bold" : "text-gray-400"}>{s.time}</span>
                  </div>
                ))}
              </div>
              <div className="mt-4 pt-3 border-t border-emerald-100">
                <div className="flex items-center gap-2 text-xs text-gray-600">
                  <i className="ri-map-pin-line text-emerald-600" />
                  <span>Willing to travel up to <strong>15 miles</strong></span>
                </div>
                <div className="flex items-center gap-2 text-xs text-gray-600 mt-1.5">
                  <i className="ri-car-line text-emerald-600" />
                  <span>Has own transport</span>
                </div>
              </div>
            </div>
          </div>

          {/* Right: Tabs — About / Jobs / Skills */}
          <div className="lg:col-span-2 flex flex-col gap-5">
            {/* Tabs */}
            <div className="flex gap-1 p-1 bg-gray-100 rounded-xl w-fit">
              {(["about", "jobs", "skills"] as const).map(tab => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`px-4 py-2 rounded-lg text-xs font-semibold capitalize transition-all cursor-pointer whitespace-nowrap ${
                    activeTab === tab ? "bg-white text-gray-900" : "text-gray-500 hover:text-gray-700"
                  }`}
                >
                  {tab === "about" ? "About Me" : tab === "jobs" ? "Past Jobs" : "Skills"}
                </button>
              ))}
            </div>

            {/* About Tab */}
            {activeTab === "about" && (
              <div className="bg-white rounded-2xl border border-gray-100 p-6 space-y-5">
                <div>
                  <h3 className="font-bold text-gray-900 text-sm mb-2">Bio</h3>
                  <p className="text-sm text-gray-600 leading-relaxed">
                    Dependable, hardworking crew member with 4+ years of experience across landscaping, warehouse
                    logistics, and event operations. Known for showing up on time, working independently, and getting
                    the job done right the first time. Open to short-term gigs and longer-term contracts.
                  </p>
                </div>
                <div>
                  <h3 className="font-bold text-gray-900 text-sm mb-3">Capabilities</h3>
                  <div className="flex flex-wrap gap-2">
                    {["Heavy Lifting (70+ lbs)", "Forklift Certified", "First Aid", "Power Tools", "Driving (CDL B)", "Team Leadership", "Inventory Mgmt", "Customer Service"].map(tag => (
                      <span key={tag} className="text-xs px-3 py-1.5 rounded-full bg-gray-100 text-gray-700 font-medium">{tag}</span>
                    ))}
                  </div>
                </div>
                <div>
                  <h3 className="font-bold text-gray-900 text-sm mb-3">Certifications</h3>
                  <div className="flex flex-col gap-2">
                    {[
                      { name: "OSHA 10 Safety",        date: "Dec 2024", icon: "ri-shield-check-line" },
                      { name: "Forklift Operator Cert", date: "Aug 2024", icon: "ri-award-line"        },
                      { name: "First Aid + CPR",        date: "Jan 2025", icon: "ri-heart-pulse-line"  },
                    ].map(c => (
                      <div key={c.name} className="flex items-center gap-3 px-3 py-2.5 rounded-xl bg-emerald-50 border border-emerald-100">
                        <i className={`${c.icon} text-emerald-600 text-sm`} />
                        <span className="text-xs font-semibold text-gray-800 flex-1">{c.name}</span>
                        <span className="text-xs text-gray-500">{c.date}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Past Jobs Tab */}
            {activeTab === "jobs" && (
              <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
                <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
                  <h3 className="font-bold text-gray-900 text-sm">Job History (38 completed)</h3>
                  <span className="text-xs text-gray-500">Showing recent 5</span>
                </div>
                <div className="divide-y divide-gray-50">
                  {PAST_JOBS.map(job => (
                    <div key={job.id} className="px-5 py-4 flex items-center gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-0.5">
                          <span className="text-sm font-bold text-gray-900">{job.title}</span>
                          <span className="text-xs bg-green-100 text-green-700 font-bold px-2 py-0.5 rounded-full">{job.status}</span>
                        </div>
                        <div className="text-xs text-gray-500">
                          {job.company} · {job.date} · {job.hours}h
                        </div>
                        <div className="flex gap-0.5 mt-1">
                          {Array.from({ length: 5 }).map((_, i) => (
                            <i key={i} className={`${i < job.rating ? "ri-star-fill" : "ri-star-line"} text-amber-400 text-xs`} />
                          ))}
                        </div>
                      </div>
                      <span className="text-sm font-extrabold text-green-700">{job.earned}</span>
                    </div>
                  ))}
                </div>
                <div className="px-5 py-4 border-t border-gray-100">
                  <Link to="/dashboard" className="text-xs font-semibold text-emerald-600 hover:underline">
                    View full history in dashboard →
                  </Link>
                </div>
              </div>
            )}

            {/* Skills Tab */}
            {activeTab === "skills" && (
              <div className="bg-white rounded-2xl border border-gray-100 p-6">
                <h3 className="font-bold text-gray-900 text-sm mb-5">Skill Proficiency</h3>
                <div className="flex flex-col gap-4">
                  {SKILLS.map(skill => (
                    <div key={skill.label}>
                      <div className="flex items-center justify-between mb-1.5">
                        <span className="text-xs font-semibold text-gray-700">{skill.label}</span>
                        <span className="text-xs font-bold text-gray-500">{skill.level}%</span>
                      </div>
                      <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                        <div
                          className={`h-full rounded-full ${skill.color} transition-all duration-700`}
                          style={{ width: `${skill.level}%` }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
